import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  type OnDestroy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { ActionButtonComponent } from '../action-button/action-button.component';

const SCROLL_STEP = 100;

enum ScrollDirection {
  Previous = -1,
  Same = 0,
  Next = 1,
  Reset = 'reset',
}

const SCROLL_INTERVAL = 300;

@Component({
  selector: 'myre-scroll-control-panel',
  templateUrl: './scroll-control-panel.component.html',
  styleUrls: ['./scroll-control-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, ActionButtonComponent],
})
export class ScrollControlPanelComponent implements OnDestroy {
  private readonly _changeDetectionRef = inject(ChangeDetectorRef);

  private _onScrollListener: (() => void) | null = null;
  private _isProgrammaticScroll = false;

  /**
   * The source container for scrolling, the one that defines the scroll position,
   * and the one on which we rely to disable previous/next actions at the end of scrolling.
   */
  @Input()
  public set scrollContainer(container: HTMLElement) {
    if (!container) {
      return;
    }

    this._removeScrollListener();
    this._scrollContainer = container;
    this._addScrollListener();

    this._scroll(ScrollDirection.Same);
  }

  /**
   * The slave containers are the ones that will be scrolled at the same time as the source container.
   */
  @Input()
  public set slaveContainers(containers: HTMLElement[] | HTMLElement) {
    if (!containers) {
      return;
    }

    if (!Array.isArray(containers)) {
      containers = [containers];
    }

    if (!containers.length) {
      return;
    }

    this._slaveContainers = containers;

    this._scroll(ScrollDirection.Same);
  }

  /**
   * Passes data on which we refresh the scroll position when the data changes.
   * This is a small trick to force the scroll to update
   * for example :
   * - if the scroll container data changes and we want to realign all the scrolls.
   * - if a slave container changes position and we want to realign all the scrolls.
   */
  @Input()
  public set refreshOnDataChange(value: any) {
    const serialized = Array.isArray(value) ? value.join(',') : value;

    if (serialized === this._lastRefreshData) {
      return;
    }

    this._lastRefreshData = serialized;
    this._scroll(ScrollDirection.Same);
  }

  protected _scrollContainer: HTMLElement;
  protected _slaveContainers: HTMLElement[] = [];

  protected _disablePrevious = false;
  protected _disableNext = false;

  private _lastRefreshData: string | undefined;

  protected _ScrollDirection = ScrollDirection;

  private _mouseDown$ = new Subject<ScrollDirection>();
  private _mouseUp$ = new Subject<void>();

  public ngOnDestroy(): void {
    this._removeScrollListener();
  }

  /**
   * Externally accessible method to reset the left scrolling position to 0.
   */
  public resetScroll(): void {
    this._scroll(ScrollDirection.Reset);
  }

  protected _mouseDown(direction: ScrollDirection): void {
    this._monitorLongClick();
    this._mouseDown$.next(direction);
  }

  protected _mouseUp(): void {
    this._mouseUp$.next();
  }

  protected _scroll(direction: ScrollDirection): void {
    let newScroll = 0;

    if (direction !== ScrollDirection.Reset) {
      newScroll = this._scrollContainer.scrollLeft + SCROLL_STEP * direction;
    }

    this._isProgrammaticScroll = true;
    this._scrollContainer.scrollLeft = newScroll;
    this._isProgrammaticScroll = false;

    this._slaveContainers.forEach(container => {
      container.scrollLeft = newScroll;
    });

    this._manageDisableButtons();
  }

  private _manageDisableButtons(): void {
    this._disablePrevious = this._scrollContainer.scrollLeft <= 0;

    this._disableNext =
      this._scrollContainer.scrollLeft + this._scrollContainer.offsetWidth >=
      this._scrollContainer.scrollWidth;
  }

  private _addScrollListener(): void {
    let rafId: number | null = null;

    const handler = (): void => {
      if (this._isProgrammaticScroll) {
        return;
      }

      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        this._slaveContainers.forEach(container => {
          container.scrollLeft = this._scrollContainer.scrollLeft;
        });

        this._manageDisableButtons();
        this._changeDetectionRef.markForCheck();
      });
    };

    this._scrollContainer.addEventListener('scroll', handler, {
      passive: true,
    });
    this._onScrollListener = () => {
      this._scrollContainer.removeEventListener('scroll', handler);

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }

  private _removeScrollListener(): void {
    this._onScrollListener?.();
    this._onScrollListener = null;
  }

  private _monitorLongClick(): void {
    let intervalId: number | null = null;

    this._mouseDown$
      .pipe(debounceTime(SCROLL_INTERVAL), takeUntil(this._mouseUp$))
      .subscribe({
        next: (direction: ScrollDirection): void => {
          intervalId = setInterval(
            () => this._scroll(direction),
            SCROLL_INTERVAL,
          ) as unknown as number;
        },
        complete: () => {
          if (intervalId) {
            clearInterval(intervalId);
          }
        },
      });
  }
}
